'use client';

import React, { useState, useRef, useEffect, useLayoutEffect, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';

export interface TooltipProps {
  /** Trigger element. Tooltip attaches to its bounding box. */
  children: React.ReactElement;
  /** Content shown in the tooltip. Keep short (1-2 sentences). */
  content: React.ReactNode;
  /** Preferred side. Falls back to opposite side if it would clip the viewport. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Delay in ms before showing on hover. Default 300ms. */
  openDelay?: number;
  /** Optional extra classes applied to the tooltip bubble. */
  className?: string;
  /** Force-disable the tooltip (e.g., on mobile). */
  disabled?: boolean;
}

/**
 * Lightweight tooltip — hover or focus to reveal a short text bubble.
 *
 * Not a replacement for Popover — for interactive content use Popover.
 * Tooltip is for purely informational hover hints.
 */
export function Tooltip({
  children,
  content,
  side = 'top',
  openDelay = 300,
  className,
  disabled = false,
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; side: typeof side } | null>(
    null,
  );
  const triggerRef = useRef<HTMLElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const approxWidth = 240;
    const approxHeight = 40;
    const bubble = bubbleRef.current;
    // Layout dimensions stay stable during the bubble's zoom-in animation;
    // getBoundingClientRect alone would briefly measure a scaled-down bubble.
    const measure = () => {
      const bounds = bubble?.getBoundingClientRect();
      return {
        width: bubble?.offsetWidth || bounds?.width || approxWidth,
        height: bubble?.offsetHeight || bounds?.height || approxHeight,
      };
    };
    if (bubble) {
      // Reset constraints before measuring so a wider viewport can restore the
      // tooltip's original class-based size.
      bubble.style.maxWidth = '';
      bubble.style.maxHeight = '';
      bubble.style.overflowY = '';
      if (measure().width > window.innerWidth - margin * 2) {
        bubble.style.maxWidth = `${Math.max(0, window.innerWidth - margin * 2)}px`;
      }
      if (measure().height > window.innerHeight - margin * 2) {
        bubble.style.maxHeight = `${Math.max(0, window.innerHeight - margin * 2)}px`;
        bubble.style.overflowY = 'auto';
      }
    }
    const { width, height } = measure();

    let resolvedSide = side;
    let top = 0;
    let left = 0;

    const tryPlace = (s: typeof side) => {
      switch (s) {
        case 'top':
          return {
            top: rect.top - height - margin,
            left: rect.left + rect.width / 2 - width / 2,
          };
        case 'bottom':
          return {
            top: rect.bottom + margin,
            left: rect.left + rect.width / 2 - width / 2,
          };
        case 'left':
          return {
            top: rect.top + rect.height / 2 - height / 2,
            left: rect.left - width - margin,
          };
        case 'right':
          return {
            top: rect.top + rect.height / 2 - height / 2,
            left: rect.right + margin,
          };
      }
    };

    const placement = tryPlace(side);
    top = placement.top;
    left = placement.left;

    // Flip if off-screen
    if (side === 'top' && top < margin) {
      resolvedSide = 'bottom';
      const alt = tryPlace('bottom');
      top = alt.top;
      left = alt.left;
    } else if (side === 'bottom' && top + height > window.innerHeight - margin) {
      resolvedSide = 'top';
      const alt = tryPlace('top');
      top = alt.top;
      left = alt.left;
    }

    // Clamp horizontally
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
    top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));

    setPosition((previous) =>
      previous?.top === top && previous.left === left && previous.side === resolvedSide
        ? previous
        : { top, left, side: resolvedSide },
    );
  }, [side]);

  const show = () => {
    if (disabled) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      computePosition();
      setIsOpen(true);
    }, openDelay);
  };

  const hide = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(false);
    setPosition(null);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    // The portal exists now: replace the initial estimate with its measured
    // dimensions, then follow viewport and ancestor-scroll changes while open.
    computePosition();
    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);
    return () => {
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    };
  }, [isOpen, computePosition]);

  // Clone the child to attach handlers without wrapping
  const child = React.cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      // Preserve existing ref on child if any
      const childAny = children as React.ReactElement & { ref?: React.Ref<HTMLElement> };
      const origRef = childAny.ref;
      if (typeof origRef === 'function') origRef(node);
      else if (origRef && typeof origRef === 'object') {
        (origRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    onMouseEnter: (e: React.MouseEvent) => {
      show();
      (children.props as { onMouseEnter?: (e: React.MouseEvent) => void }).onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hide();
      (children.props as { onMouseLeave?: (e: React.MouseEvent) => void }).onMouseLeave?.(e);
    },
    onFocus: (e: React.FocusEvent) => {
      show();
      (children.props as { onFocus?: (e: React.FocusEvent) => void }).onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent) => {
      hide();
      (children.props as { onBlur?: (e: React.FocusEvent) => void }).onBlur?.(e);
    },
    'aria-describedby':
      [(children.props as React.AriaAttributes)['aria-describedby'], isOpen ? tooltipId : undefined]
        .filter(Boolean)
        .join(' ') || undefined,
  } as Record<string, unknown>);

  const bubble =
    isOpen && position && typeof window !== 'undefined'
      ? createPortal(
          <div
            ref={bubbleRef}
            id={tooltipId}
            role="tooltip"
            aria-live="polite"
            // Same stacking bug as the dropdowns (2026-08-26): portalled at z-60, below
            // every dialog (z-[200]+), so a tooltip inside a modal never appeared. 290
            // keeps it above the overlays and just under an open dropdown panel.
            style={{ position: 'fixed', top: position.top, left: position.left, zIndex: 290 }}
            className={cn(
              'max-w-xs rounded-overlay bg-surface-inverse px-3 py-2 text-caption font-medium text-text-inverse shadow-popover',
              'animate-in fade-in-0 zoom-in-95 duration-150',
              className,
            )}
          >
            {content}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {child}
      {bubble}
    </>
  );
}
